import * as React from 'react';
import styled from 'styled-components';
import Text from 'crehana-ui/Text';

import getImgxUrl from 'Jsx/Utils/getImgxUrl';
import Clear from 'crehana-icons/Clear';
import MediumClear from 'crehana-icons/MediumClear';
import Plus from 'crehana-icons/Plus2';

import { CourseProps } from 'Jsx/Shared/CourseCardV3/types';
import useMediaQuery from 'Jsx/Shared/Hooks/useMediaQuery';

import CoursePreviewContent from './CoursePreviewContent';

const PackImage = styled.div<{ imageUrl: string }>`
  position: relative;
  overflow: hidden;
  background: #f7faff;
  background-image: url(${props => props.imageUrl});
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  display: inline-block;
`;

export const Triangle = styled.div`
  border-right: 16px solid transparent;
  border-left: 16px solid transparent;
  border-bottom: 16px solid #fff;
  border-top: 0;
  font-size: 0;
  height: 0;
  line-height: 0;
  margin: 0;
  padding: 0;
  position: absolute;
  top: -16px;
  width: 0;
  left: 49%;
  &:after {
    position: relative;
    content: '';
    display: block;
    width: 0;
    height: 0;
    top: -1px;
    left: -16px;
    z-index: -1;
    border-bottom: 16px solid rgba(185, 185, 185, 0.25);
    border-right: 16px solid transparent;
    border-left: 16px solid transparent;
  }
`;

const Opacity = styled.div`
  background: linear-gradient(
    242.71deg,
    rgba(24, 27, 50, 0.5) 0%,
    rgba(0, 0, 0, 0) 100%
  );
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
`;

type Props = {
  index: number;
  courses: CourseProps[];
  course: CourseProps;
  removeCourseFromPack: (course: CourseProps) => void;
};

const CoursesInPackItem: React.FC<Props> = ({
  courses,
  index,
  course,
  removeCourseFromPack,
}) => {
  const { matches } = useMediaQuery('md');
  const [showPreview, setShowPreview] = React.useState(false);
  const coursePackRef = React.useRef(null);

  const showPreviewHandler = () => {
    setShowPreview(true);
  };

  const hidePreviewHandler = () => {
    setShowPreview(false);
  };

  React.useEffect(() => {
    const onKeyEscape = ({ key }) => {
      if (key === 'Escape' && showPreview) {
        setShowPreview(false);
      }
    };

    if (showPreview) {
      document.addEventListener('keydown', onKeyEscape);
    }

    return () => {
      document.removeEventListener('keydown', onKeyEscape);
    };
  }, [showPreview]);

  return (
    <div
      key={course ? course.originalId : undefined}
      className={`flex items-center relative ${
        index !== 0 ? 'ml-12 md:ml-0' : ''
      }`}
    >
      {course ? (
        <div
          className="w-56 h-56 sm:w-80 sm:h-56 relative flex items-center justify-center"
          onMouseEnter={showPreviewHandler}
          onMouseLeave={hidePreviewHandler}
          ref={coursePackRef}
        >
          <PackImage
            className="w-full h-full"
            imageUrl={getImgxUrl(
              (course.promoImage || course.promoImageAnimated) as string,
              {
                w: 80,
                h: 56,
              },
            )}
          />
          <Opacity />
          <button
            type="button"
            className={`absolute ${matches ? 'top-0 right-0 p-8' : ''}`}
            onClick={() => {
              removeCourseFromPack(course);
            }}
          >
            <MediumClear size={matches ? 12 : 24} color="#fff" />
          </button>
          {/* TODO: Add enter/exit animation */}
          {matches && showPreview && (
            <div
              className={`absolute ${
                showPreview
                  ? 'opacity-100 pointer-events-auto'
                  : 'pointer-events-none opacity-0'
              }`}
              style={{
                left: '-172px',
                top: '100%',
                width: '400px',
                zIndex: 99,
                paddingTop: 16,
                backgroundColor: 'transparent',
                transition: 'opacity 0.2s ease-in-out',
              }}
            >
              <div
                className="h-full bg-white shadow-snackbar p-32 relative"
                style={{
                  width: '400px',
                }}
              >
                <Triangle />
                <button
                  type="button"
                  onClick={hidePreviewHandler}
                  color="#8D8D9D"
                  className="absolute h-18 w-18"
                  style={{ top: '15px', right: '15px' }}
                >
                  <Clear size={18} />
                </button>
                <CoursePreviewContent
                  wistiaLoad
                  data={{ customBundle: true, ...course }}
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="w-56 h-56 sm:w-80 bg-gray-lighter flex items-center justify-center">
          <Text type="h4" color="gray-main">
            {index + 1}
          </Text>
        </div>
      )}
      {matches && courses.length > index + 1 && (
        <span className="px-12">
          <Plus size={12} />
        </span>
      )}
    </div>
  );
};

export default CoursesInPackItem;
